import useIsMobile from 'utils/hooks/useIsMobile'
import useWindowWidth from 'utils/hooks/useWindowWidth'
import {envBackHost, serverLocalName} from 'GLOBAL/envVariables'
import {ArcElement, Chart as ChartJS, Legend, Tooltip} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import {axiosWithAuth} from 'helper/axiosWithAuth'
import _ from 'lodash'
import {customerLeakageFilter} from 'pages/customer/dataConfig/declearFilters'
import {useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {normalAlert} from 'utils/CustomAlert/Alert'
import {leakModelHandler} from 'utils/const/globalController'

import {windowWidthResize} from 'utils/func/justUtils'
import {customerLeakageFilterType} from 'utils/interface/customerType'
import StatusChartCard, {ChartLegendItem} from './StatusChartCard'

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels)

const DashboardChart = () => {
  const [filter, setFilter] = useState<customerLeakageFilterType>(customerLeakageFilter)
  const [customerInfoFilter, setCustomerInfoFilter] = useState<any>({
    searchOption: {
      customerNumber: false,
      customerName: false,
      address: false,
      managerName: false,
      memo: false,
      serialNumber: false,
      installerName: false,
    },
    searchData: '',
    deviceStatus: 'all',
    efosStatus: 'all',
    deviceType: 'all',
    batteryStatus: 'all',
    rowPerPage: 30,
    page: 1,
  })

  const [chartData, setChartData] = useState<any>({
    deviceStatus: {
      bad: 0,
      deviceBad: 0,
      deviceComplete: 0,
      deviceUpdate: 0,
      digitalMeterBad: 0,
      efosBad: 44,
      imageProcess: null,
      imageProcessCount: 0,
      normal: 2400,
    },
    batteryStatus: null,
    deviceTypeStatus: null,
    frozenStatus: [
      {freezeWarningCount: 0, freezeWarningTypeName: '양호'},
      {freezeWarningCount: 0, freezeWarningTypeName: '주의'},
      {freezeWarningCount: 0, freezeWarningTypeName: '위험'},
      {freezeWarningCount: 0, freezeWarningTypeName: '매우위험'},
    ],
    abnormalStatus: null,
  })

  const officeInfo = useSelector((state: any) => state.officeInfo)
  const userInfo = useSelector((state: any) => state.userInfo?.userInfo)
  const cooperInfo = useSelector((state: any) => state.cooperInfo)
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)
  const isMobile = useIsMobile()
  const windowWidth = useWindowWidth()

  const DeviceStatusdata = {
    labels: ['정상', '검침영상플레이어', '원격검침단말기', '디지털계량기'],
    datasets: [
      {
        label: '# of Votes',
        data: [
          chartData?.deviceStatus?.normal,
          chartData?.deviceStatus?.efosBad,
          chartData?.deviceStatus?.deviceBad,
          chartData?.deviceStatus?.digitalMeterBad,
        ],
        backgroundColor: [
          'rgba(54, 162, 235, 0.7)',
          'rgba(137,35,245,0.7)',
          'rgba(255, 99, 132, 0.7)',
          'rgba(75, 192, 192, 0.7)',
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(137,35,245,1)',
          'rgba(255, 99, 132, 1)',
          'rgba(75, 192, 192, 1)',
        ],
        borderWidth: 1,
      },
    ],
  }
  const batteryChartData = {
    labels: ['정상', '점검'],
    datasets: [
      {
        label: '# of Votes',
        data: [chartData?.batteryStatus?.normal, chartData?.batteryStatus?.bad],
        backgroundColor: ['rgba(54, 162, 235, 0.7)', 'rgba(255, 99, 132, 0.7)'],
        borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)'],
        borderWidth: 1,
      },
    ],
  }
  const DeviceTypeData = {
    labels: chartData?.deviceTypeStatus?.map((el: any) => el?.deviceTypeName),
    datasets: [
      {
        label: '# of Votes',
        data: chartData?.deviceTypeStatus?.map((el: any) => el?.typeCount),
        backgroundColor: ['rgba(252,98,9,0.7)', 'rgba(75, 192, 192, 0.7)'],
        borderColor: ['rgba(252,98,9,1)', 'rgba(75, 192, 192, 1)'],
        borderWidth: 1,
      },
    ],
  }

  const freezeWarningData = {
    labels: chartData?.frozenStatus?.map((el: any) => el?.freezeWarningTypeName),
    datasets: [
      {
        label: '# of Votes',
        data: chartData?.frozenStatus?.map((el: any) => el?.freezeWarningCount),
        backgroundColor: [
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 215, 0, 0.7)',
          'rgba(255, 165, 0, 0.7)',
          'rgba(210, 105, 30, 0.7)',
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 215, 0, 1)',
          'rgba(255, 165, 0, 1)',
          'rgba(210, 105, 30, 1)',
        ],
        borderWidth: 1,
      },
    ],
  }

  const abnormalData: any = {
    labels: chartData?.abnormalStatus?.map((el: any) => el?.typeName),
    datasets: [
      {
        label: '# of Votes',
        data: chartData?.abnormalStatus?.map((el: any) => el?.count),
        backgroundColor: ['rgba(54, 162, 235, 0.7)', 'rgba(255, 99, 132, 0.7)'],
        borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)'],
        borderWidth: 1,
      },
    ],
  }

  const DeviceStatusdataTotal = DeviceStatusdata?.datasets[0]?.data?.reduce(
    (acc, val) => acc + val,
    0
  )
  const batteryChartDataTotal = batteryChartData?.datasets[0]?.data?.reduce(
    (acc, val) => acc + val,
    0
  )
  const DeviceTypeDataTotal = DeviceTypeData?.datasets[0]?.data?.reduce(
    (acc: any, val: any) => acc + val,
    0
  )
  const freezeWaringDataTotal = freezeWarningData?.datasets[0]?.data?.reduce(
    (acc: any, val: any) => acc + val,
    0
  )
  const abnormalDataTotal = abnormalData?.datasets[0]?.data?.reduce(
    (acc: any, val: any) => acc + val,
    0
  )

  const statusMap: Record<number, any> = {
    0: {efosStatus: 'true', deviceStatus: 'true'},
    1: {efosStatus: 'false', deviceStatus: 'true', recognizedType: 'analog'},
    2: {deviceStatus: 'false'},
    3: {efosStatus: 'false', deviceStatus: 'true', recognizedType: 'digital'},
  }
  const freezeWarningTypeMap: Record<string, string> = {
    양호: 'normal',
    주의: 'warning',
    위험: 'dangerous',
    매우위험: 'very-dangerous',
  }

  const isSys = userInfo?.accountType?.includes('sys')
  const isGovAdmin = userInfo?.accountType?.includes('gov_admin')
  /* gov_admin 과 동일하게 '수용가 현황'으로 이동시킬 계정.
     AsideMenuMain 에서 수용가 현황 메뉴를 공유하는 그룹과 동일하다. */
  const customerStatusAccounts = ['gov_meterman', 'gov_meterman_editable', 'gov_coop', 'manu_cust']
  const canUseCustomerStatus = Boolean(
    isGovAdmin || customerStatusAccounts.includes(userInfo?.accountType)
  )
  /* 통신상태/배터리/원격검침 이동 권한. 권한 없는 계정에서는
     모바일 타일을 비활성으로 렌더해 권한 경고 반복을 막는다. */
  const canNavigateCustomer = Boolean(isSys || canUseCustomerStatus)

  /**
   * 차트 조각 클릭과 우측 범례 클릭이 공유하는 이동 핸들러.
   * (index 로 라벨을 찾아 권한별로 라우팅)
   */
  const navigateDeviceStatus = (index: number) => {
    if (isSys) {
      navigate('/customer/info', {state: statusMap[index] || {}})
    } else if (canUseCustomerStatus) {
      /* sys 와 동일한 statusMap 조건을 넘겨 타일 건수와 목록 건수를 일치시킨다. */
      navigate('/customer/customer-status', {state: statusMap[index] || {}})
    } else {
      normalAlert('권한이 없습니다.')
    }
  }
  const navigateBattery = (index: number) => {
    const isNormal = batteryChartData.labels[index] === '정상'
    if (isSys) {
      navigate('/customer/info', {state: {batteryStatus: isNormal ? 'true' : 'false'}})
    } else if (canUseCustomerStatus) {
      navigate('/customer/customer-status', {state: {batteryStatus: isNormal ? 'true' : 'false'}})
    } else {
      normalAlert('권한이 없습니다.')
    }
  }
  const navigateDeviceType = (index: number) => {
    const type = DeviceTypeData?.labels?.[index] === '디지털이미지' ? 'analog' : 'digital'
    if (isSys) {
      navigate('/customer/info', {state: {deviceType: type}})
    } else if (canUseCustomerStatus) {
      navigate('/customer/customer-status', {state: {deviceType: type}})
    } else {
      normalAlert('권한이 없습니다.')
    }
  }
  const navigateFreeze = (index: number) => {
    const type = freezeWarningTypeMap[freezeWarningData?.labels?.[index]]
    if (type) {
      navigate('/customer/freeze-warning', {state: {freezeWarningType: type}})
    }
  }
  const navigateAbnormal = (index: number) => {
    const label = abnormalData?.labels?.[index]
    if (label === '누수의심') {
      navigate('/customer/customer-leakage')
    } else if (label === '장기미사용') {
      navigate('/customer/unused')
    }
  }

  /**
   * 5개 차트가 공유하는 chart.js 옵션 팩토리.
   * title/total/onSliceClick 과 도넛/막대 여부만 다르다.
   */
  const buildChartOptions = ({
    title,
    total,
    onSliceClick,
    variant,
  }: {
    title: string
    total: number
    onSliceClick: (index: number) => void
    variant: 'doughnut' | 'bar'
  }) => {
    const size = windowWidthResize(windowWidth)
    const options: any = {
      responsive: true,
      aspectRatio: 1,
      onClick(event: any, activeElements: any) {
        if (activeElements && activeElements.length > 0) {
          onSliceClick(activeElements[0].index)
        }
      },
      plugins: {
        legend: {
          display: false,
          position: 'right',
        },
        tooltip: {
          enabled: variant !== 'bar',
          callbacks: {
            label: function (context: any) {
              return context?.raw + '건'
            },
            title: function (context: any) {
              return context[0].label
            },
          },
          bodyFont: {size},
          bodyAlign: 'center',
          bodyClassName: 'align-self-center mx-2',
          titleFont: {size},
          titleAlign: 'center',
          titleMarginBottom: 10,
          backgroundColor: 'rgba(37,37,37,0.75)',
        },
        title: {
          display: true,
          text: title,
          font: {size},
          position: 'top',
        },
        datalabels: {
          display: true,
          color: 'black',
          align: 'center',
          anchor: 'center',
          font: {size, weight: 'bold'},
          formatter: function (value: any) {
            if (variant === 'bar') {
              return value >= 5 ? value + '건' : null
            }
            const percentage = (value / total) * 100
            return percentage >= 5 ? value + '건' : null
          },
        },
      },
    }
    if (variant === 'bar') {
      options.scales = {
        x: {display: false},
        y: {display: false},
      }
    }
    return options
  }

  /* 모바일 타일 헤더에서도 쓰는 차트 제목 */
  const chartTitles = {
    deviceStatus: '통신상태 점검 현황',
    battery: '배터리 현황',
    deviceType: '원격검침 현황',
    freeze: '동파경보 현황',
    abnormal: '점검 필요 현황',
  }

  const deviceChartOptions = buildChartOptions({
    title: `${chartTitles.deviceStatus} ( 총 ${DeviceStatusdataTotal}건 )`,
    total: DeviceStatusdataTotal,
    onSliceClick: navigateDeviceStatus,
    variant: 'doughnut',
  })
  const batteryChartOptions = buildChartOptions({
    title: `${chartTitles.battery} ( 총 ${batteryChartDataTotal}건 )`,
    total: batteryChartDataTotal,
    onSliceClick: navigateBattery,
    variant: 'doughnut',
  })
  const deviceTypeCahrtOptions = buildChartOptions({
    title: `${chartTitles.deviceType} ( 총 ${DeviceTypeDataTotal}건 )`,
    total: DeviceTypeDataTotal,
    onSliceClick: navigateDeviceType,
    variant: 'doughnut',
  })
  const freezeWaringCahrtOptions = buildChartOptions({
    title: `${chartTitles.freeze} ( 총 ${freezeWaringDataTotal}건 )`,
    total: freezeWaringDataTotal,
    onSliceClick: navigateFreeze,
    variant: 'doughnut',
  })
  const abnormalDataChartOptions = buildChartOptions({
    title: `${chartTitles.abnormal} ( 총 ${loading ? abnormalDataTotal : 0}건 )`,
    total: abnormalDataTotal,
    onSliceClick: navigateAbnormal,
    variant: 'bar',
  })

  const buildLegend = (
    labels: any[],
    data: any[],
    colors: any[],
    onClick: (index: number) => void
  ): ChartLegendItem[] =>
    _.map(labels, (label, i) => ({
      label,
      count: data?.[i],
      color: colors?.[i],
      onClick: () => onClick(i),
    }))

  const deviceStatusLegend = buildLegend(
    DeviceStatusdata.labels,
    DeviceStatusdata.datasets[0].data,
    DeviceStatusdata.datasets[0].borderColor,
    navigateDeviceStatus
  )
  const batteryLegend = buildLegend(
    batteryChartData.labels,
    batteryChartData.datasets[0].data,
    batteryChartData.datasets[0].borderColor,
    navigateBattery
  )
  const deviceTypeLegend = buildLegend(
    DeviceTypeData.labels,
    DeviceTypeData.datasets[0].data,
    DeviceTypeData.datasets[0].borderColor,
    navigateDeviceType
  )
  const freezeLegend = buildLegend(
    freezeWarningData.labels,
    freezeWarningData.datasets[0].data,
    freezeWarningData.datasets[0].borderColor,
    navigateFreeze
  )
  const abnormalLegend = buildLegend(
    abnormalData.labels,
    abnormalData.datasets[0].data,
    abnormalData.datasets[0].borderColor,
    navigateAbnormal
  )

  const updateChartData = (field: any, newData: any) => {
    setChartData((prevChartData: any) => ({...prevChartData, [field]: newData}))
  }
  const getDeviceStatus = () => {
    axiosWithAuth()
      .get(
        `${envBackHost}/${officeInfo?.value === 'yit' ? 'all' : officeInfo?.value}/${
          cooperInfo?.value
        }/dashboard/devicestatus`
      )
      .then((res) => {
        updateChartData('deviceStatus', res.data.res)
      })
  }
  const getDeviceBatteryStatus = () => {
    axiosWithAuth()
      .get(
        `${envBackHost}/${officeInfo?.value === 'yit' ? 'all' : officeInfo?.value}/${
          cooperInfo?.value
        }/dashboard/devicebattery`
      )
      .then((res) => {
        updateChartData('batteryStatus', res.data.res)
      })
  }
  const getDeviceTypeStatus = () => {
    axiosWithAuth()
      .get(
        `${envBackHost}/${officeInfo?.value === 'yit' ? 'all' : officeInfo?.value}/${
          cooperInfo.value
        }/dashboard/devicetype`
      )
      .then((res) => {
        updateChartData('deviceTypeStatus', res.data.res)
      })
  }
  const getFrozenStatus = () => {
    axiosWithAuth()
      .get(
        `${envBackHost}/${officeInfo?.value === 'yit' ? 'all' : officeInfo?.value}/${
          cooperInfo.value
        }/dashboard/freezewarningcount`
      )
      .then((res) => {
        updateChartData('frozenStatus', res.data.res)
      })
  }

  const getAbnormalStatus = () => {
    setLoading(false)
    axiosWithAuth()
      .get(
        `${envBackHost}/${officeInfo?.value === 'yit' ? 'all' : officeInfo?.value}/${
          cooperInfo.value
        }/dashboard/leakandlongtermcount`,
        {
          params: filter,
        }
      )
      .then((res) => {
        updateChartData('abnormalStatus', res.data.res)
        setLoading(true)
      })
  }

  useEffect(() => {
    /* 동파경보/점검필요 차트는 모바일에서 렌더하지 않으므로 조회도 건너뛴다 */
    if (!isMobile) {
      getFrozenStatus()
      if (leakModelHandler(serverLocalName, officeInfo.value) === '2') {
        getAbnormalStatus()
      }
    }
    getDeviceStatus()
    getDeviceBatteryStatus()
    getDeviceTypeStatus()
  }, [officeInfo, cooperInfo, isMobile])

  return (
    <div
      className={isMobile ? 'w-100 my-3' : 'card card-body w-100 my-5 p-0 mx-0 d-flex col'}
      style={isMobile ? undefined : {background: 'none'}}
    >
      <div className={isMobile ? 'flex flex-col gap-2' : 'd-flex flex-wrap px-0 mx-0 gap-4 col'}>
        <StatusChartCard
          chartType='doughnut'
          data={DeviceStatusdata}
          options={deviceChartOptions}
          chartId='deviceStatus-chart'
          legendId='deviceStatus-info'
          legend={deviceStatusLegend}
          cardPadding='p-1'
          legendFontClass='fs-3'
          legendItemWidth={300}
          legendItemMarginLeft='30px'
          title={chartTitles.deviceStatus}
          total={DeviceStatusdataTotal}
          interactive={canNavigateCustomer}
        />
        <StatusChartCard
          chartType='doughnut'
          data={batteryChartData}
          options={batteryChartOptions}
          chartId='deviceBattery-chart'
          legendId='deviceBattery-info'
          legend={batteryLegend}
          legendItemWidth={300}
          legendItemMarginLeft='30px'
          title={chartTitles.battery}
          total={batteryChartDataTotal}
          interactive={canNavigateCustomer}
        />
        <StatusChartCard
          chartType='doughnut'
          data={DeviceTypeData}
          options={deviceTypeCahrtOptions}
          chartId='deviceType-chart'
          legendId='deviceType-info'
          legend={deviceTypeLegend}
          legendItemWidth={300}
          legendItemMarginLeft='30px'
          title={chartTitles.deviceType}
          total={DeviceTypeDataTotal}
          interactive={canNavigateCustomer}
        />
        {/* 동파경보 / 점검 필요 현황은 이동 대상 페이지
            (/customer/freeze-warning, /customer/customer-leakage, /customer/unused)의
            모바일 레이아웃이 아직 없어 모바일에서는 노출하지 않는다.
            해당 페이지들이 모바일 대응되면 !isMobile 조건만 제거하면 된다. */}
        {!isMobile && (
          <StatusChartCard
            chartType='doughnut'
            data={freezeWarningData}
            options={freezeWaringCahrtOptions}
            chartId='freezeWarning-chart'
            legendId='freezeWarning-info'
            legend={freezeLegend}
            legendFontClass='fs-3'
            legendItemWidth={280}
            legendItemMarginLeft='30px'
            title={chartTitles.freeze}
            total={freezeWaringDataTotal}
          />
        )}
        {!isMobile &&
          leakModelHandler(serverLocalName, officeInfo.value) === '2' &&
          officeInfo?.value !== 'yit' &&
          officeInfo?.value !== 'all' && (
            <StatusChartCard
              chartType='bar'
              data={abnormalData}
              options={abnormalDataChartOptions}
              chartId='abnormal-chart'
              legendId='abnormal-info'
              legend={abnormalLegend}
              legendFontClass='fs-2'
              legendItemWidth={240}
              loading={loading}
              loadingGate
              title={chartTitles.abnormal}
              total={abnormalDataTotal}
            />
          )}
      </div>
    </div>
  )
}

export default DashboardChart
